import { t as __exportAll } from "./chunk-CfYAbeIz.mjs";
import { alpha, createTheme } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import { createStyled } from "@mui/system";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, SvgIcon, Switch as Switch$1, Typography, backdropClasses, dialogClasses } from "@mui/material";
import { jsx, jsxs } from "react/jsx-runtime";
import MuiIconButton from "@mui/material/IconButton";
import * as React from "react";
import { Close } from "@mui/icons-material";
import { mergeSlotProps } from "@mui/material/utils";
import { ErrorBoundary } from "react-error-boundary";
import FocusLock from "react-focus-lock";
//#region src/theme.base.ts
const poppinsFontFamily = ["\"Poppins\"", "serif"].join(",");
const interFontFamily = ["\"Inter\"", "serif"].join(",");
const avatarOverrides = {
	width: 32,
	height: 32,
	fontSize: "1rem",
	fontWeight: 400,
	lineHeight: 1.25,
	letterSpacing: "0.03em"
};
/**
* Shared theme options used in both light and dark themes. These options are
* not exhaustive, all the omitted options still use the defaults provided by
* `mui`.
*
* @see https://v5.mui.com/material-ui/customization/default-theme/
* @see https://v5.mui.com/material-ui/customization/theming/
*/
const themeOptions = {
	spacing: 8,
	shape: { borderRadius: 4 },
	palette: {
		tertiary: {
			light: "#ef874d",
			main: "#ec6a21",
			dark: "#a54a17",
			contrastText: "#ffffff"
		},
		quaternary: {
			light: "#788edc",
			main: "#5772d4",
			dark: "#3c4f94",
			contrastText: "#ffffff"
		}
	},
	typography: {
		fontFamily: interFontFamily,
		h1: {
			fontFamily: poppinsFontFamily,
			fontSize: "6rem",
			fontWeight: 500,
			lineHeight: 1.167,
			letterSpacing: "0.03em"
		},
		h2: {
			fontFamily: poppinsFontFamily,
			fontSize: "3.75rem",
			fontWeight: 500,
			lineHeight: 1.2,
			letterSpacing: "0.03em"
		},
		h3: {
			fontFamily: poppinsFontFamily,
			fontSize: "3rem",
			fontWeight: 500,
			lineHeight: 1.17,
			letterSpacing: "0.03em"
		},
		h4: {
			fontFamily: poppinsFontFamily,
			fontSize: "2.125rem",
			fontWeight: 500,
			lineHeight: 1.235,
			letterSpacing: "0.03em"
		},
		h5: {
			fontFamily: poppinsFontFamily,
			fontSize: "1.5rem",
			fontWeight: 600,
			lineHeight: 1.334,
			letterSpacing: "0.03em"
		},
		h6: {
			fontFamily: poppinsFontFamily,
			fontSize: "1.25rem",
			fontWeight: 600,
			lineHeight: 1.5,
			letterSpacing: "0.03em"
		},
		subtitle1: {
			fontFamily: interFontFamily,
			fontSize: "1.125rem",
			fontWeight: 500,
			lineHeight: 1.4,
			letterSpacing: "0.03em"
		},
		subtitle2: {
			fontFamily: interFontFamily,
			fontSize: "1rem",
			fontWeight: 500,
			lineHeight: 1.45,
			letterSpacing: "0.03em"
		},
		body1: {
			fontFamily: interFontFamily,
			fontSize: "1rem",
			fontWeight: 400,
			lineHeight: 1.4,
			letterSpacing: "0.03em"
		},
		body2: {
			fontFamily: interFontFamily,
			fontSize: "0.875rem",
			fontWeight: 400,
			lineHeight: 1.4,
			letterSpacing: "0.03em"
		},
		button: {
			fontFamily: interFontFamily,
			fontWeight: 500,
			textTransform: "capitalize",
			letterSpacing: "0.03em"
		},
		caption: {
			fontFamily: interFontFamily,
			fontSize: "0.75rem",
			fontWeight: 500,
			lineHeight: 1.6,
			letterSpacing: "0.03em"
		},
		overline: {
			fontFamily: interFontFamily,
			fontSize: "0.75rem",
			fontWeight: 400,
			lineHeight: 2.5,
			letterSpacing: "0.06em"
		}
	},
	components: {
		MuiAvatar: { styleOverrides: { root: (props) => ({
			...avatarOverrides,
			backgroundColor: props.theme.palette.primary.main,
			color: props.theme.palette.primary.contrastText
		}) } },
		MuiAvatarGroup: { styleOverrides: { avatar: (props) => ({
			...avatarOverrides,
			backgroundColor: props.theme.palette.primary.main,
			color: props.theme.palette.primary.contrastText
		}) } },
		MuiAlert: {
			defaultProps: { variant: "filled" },
			styleOverrides: { message: {
				fontSize: "0.875rem",
				fontWeight: 400,
				lineHeight: 1.43,
				letterSpacing: "0.03em"
			} }
		},
		MuiAlertTitle: { styleOverrides: { root: {
			fontWeight: 600,
			color: "inherit"
		} } },
		MuiBadge: { styleOverrides: { badge: {
			fontWeight: 600,
			lineHeight: 1.25
		} } },
		MuiButton: {
			variants: [
				{
					props: { size: "small" },
					style: {
						fontSize: "0.8125rem",
						lineHeight: 1.375
					}
				},
				{
					props: { size: "medium" },
					style: {
						fontSize: "0.875rem",
						lineHeight: 1.5
					}
				},
				{
					props: { size: "large" },
					style: {
						fontSize: "0.9375rem",
						lineHeight: 1.625
					}
				},
				{
					props: { size: "extra-large" },
					style: {
						padding: "12px 32px",
						fontSize: "1.125rem",
						lineHeight: 1.75
					}
				},
				{
					props: {
						size: "extra-large",
						variant: "outlined"
					},
					style: { padding: "11px 31px" }
				}
			],
			defaultProps: {
				variant: "contained",
				color: "primary"
			}
		},
		MuiCard: { styleOverrides: { root: (props) => ({ borderRadius: Number(props.theme.shape.borderRadius) * 2.5 }) } },
		MuiCheckbox: { defaultProps: { size: "small" } },
		MuiChip: { styleOverrides: {
			label: {
				lineHeight: 1.125,
				letterSpacing: "0.03em"
			},
			icon: {
				width: "0.8em",
				height: "0.8em"
			}
		} },
		MuiFormHelperText: { styleOverrides: { root: {
			fontWeight: 400,
			lineHeight: 1.25
		} } },
		MuiFormLabel: { styleOverrides: { asterisk: (props) => ({ color: props.theme.palette.error.main }) } },
		MuiInputBase: { styleOverrides: { root: {
			lineHeight: 1.5,
			letterSpacing: "0.03em"
		} } },
		MuiInputLabel: { styleOverrides: { root: {
			fontWeight: 500,
			letterSpacing: "0.03em"
		} } },
		MuiLink: { styleOverrides: { root: (props) => ({ color: props.theme.palette.info.main }) } },
		MuiRadio: { defaultProps: { size: "small" } },
		MuiStack: { defaultProps: {
			direction: "row",
			spacing: 1
		} },
		MuiTableCell: { styleOverrides: { root: { padding: 12 } } },
		MuiTablePagination: { defaultProps: {
			showFirstButton: true,
			showLastButton: true
		} },
		MuiTab: { styleOverrides: { iconWrapper: {
			width: "0.9em",
			height: "0.9em"
		} } },
		MuiTextField: { defaultProps: { size: "small" } },
		MuiTooltip: { defaultProps: {
			arrow: true,
			placement: "top"
		} },
		MuiTypography: { styleOverrides: { root: (props) => ({ color: props.theme.palette.text.primary }) } }
	}
};
const theme = createTheme(themeOptions);
//#endregion
//#region src/theme.dark.ts
const themeOptions$1 = {
	palette: {
		mode: "dark",
		primary: { main: "#2ba3c3" },
		secondary: { main: "#d81f5b" },
		success: { main: "#57d489" },
		error: { main: "#ff5757" },
		warning: { main: "#f9da6e" },
		info: { main: "#5cecfe" },
		neutral: {
			light: "#d7dadd",
			main: "#cdd1d5",
			dark: "#8f9295",
			contrastText: "#313131"
		},
		muted: {
			1: "#acb3b9",
			2: "#dde1e3",
			3: "#f0f0f0",
			4: "#d7dfe9",
			5: "#eceff3",
			6: "#f0f2f6"
		},
		background: {
			paper: "#052d3d",
			default: "#012331"
		},
		text: {
			primary: "#fbfbfb",
			secondary: "#a2b9c2",
			disabled: "#a0a0a0e6"
		}
	},
	components: {
		MuiAlert: { variants: [
			{
				props: {
					variant: "standard",
					severity: "error"
				},
				style: {
					backgroundColor: "#591817",
					color: "#ffbcbc"
				}
			},
			{
				props: {
					variant: "filled",
					severity: "error"
				},
				style: (props) => ({
					backgroundColor: props.theme.palette.error.main,
					color: props.theme.palette.error.contrastText
				})
			},
			{
				props: {
					variant: "outlined",
					severity: "error"
				},
				style: (props) => ({
					borderColor: props.theme.palette.error.main,
					color: "#ffbcbc"
				})
			},
			{
				props: {
					variant: "standard",
					severity: "warning"
				},
				style: {
					backgroundColor: "#5d4719",
					color: "#fdf0c5"
				}
			},
			{
				props: {
					variant: "filled",
					severity: "warning"
				},
				style: (props) => ({
					backgroundColor: props.theme.palette.warning.main,
					color: props.theme.palette.warning.contrastText
				})
			},
			{
				props: {
					variant: "outlined",
					severity: "warning"
				},
				style: (props) => ({
					borderColor: props.theme.palette.warning.main,
					color: "#fdf0c5"
				})
			},
			{
				props: {
					variant: "standard",
					severity: "info"
				},
				style: {
					backgroundColor: "#1c4b6a",
					color: "#bef7ff"
				}
			},
			{
				props: {
					variant: "filled",
					severity: "info"
				},
				style: (props) => ({
					backgroundColor: props.theme.palette.info.main,
					color: props.theme.palette.info.contrastText
				})
			},
			{
				props: {
					variant: "outlined",
					severity: "info"
				},
				style: (props) => ({
					borderColor: props.theme.palette.info.main,
					color: "#bef7ff"
				})
			},
			{
				props: {
					variant: "standard",
					severity: "success"
				},
				style: {
					backgroundColor: "#1a5432",
					color: "#bceed0"
				}
			},
			{
				props: {
					variant: "filled",
					severity: "success"
				},
				style: (props) => ({
					backgroundColor: props.theme.palette.success.main,
					color: props.theme.palette.success.contrastText
				})
			},
			{
				props: {
					variant: "outlined",
					severity: "success"
				},
				style: (props) => ({
					borderColor: props.theme.palette.success.main,
					color: "#bceed0"
				})
			}
		] },
		MuiListItemIcon: { styleOverrides: { root: (props) => ({ color: alpha(props.theme.palette.common.white, .56) }) } },
		MuiSlider: { styleOverrides: { valueLabel: (props) => ({
			padding: props.theme.spacing(.5, 1),
			fontSize: "0.75rem",
			fontWeight: 400,
			lineHeight: 1.16,
			letterSpacing: "0.03em",
			backgroundColor: props.theme.palette.muted[2],
			color: props.theme.palette.common.black
		}) } },
		MuiTooltip: { styleOverrides: {
			tooltip: (props) => ({
				padding: props.theme.spacing(.5, 1),
				fontSize: "0.75rem",
				fontWeight: 400,
				lineHeight: 1.16,
				letterSpacing: "0.03em",
				backgroundColor: "#3a6982",
				color: props.theme.palette.common.white
			}),
			arrow: {
				color: "#3a6982",
				borderColor: "#3a6982"
			}
		} }
	}
};
const theme$1 = createTheme(deepmerge(themeOptions, themeOptions$1));
//#endregion
//#region src/theme.light.ts
const themeOptions$2 = {
	palette: {
		mode: "light",
		primary: { main: "#166280" },
		secondary: { main: "#ec2663" },
		success: { main: "#2f7e4f" },
		error: { main: "#db3b21" },
		warning: { main: "#8e7106" },
		info: { main: "#0044cc" },
		neutral: {
			light: "#8c8f92",
			main: "#707477",
			dark: "#4e5153",
			contrastText: "#ffffffe6"
		},
		muted: {
			1: "#363b3f",
			2: "#515659",
			3: "#f0f0f0",
			4: "#31404e",
			5: "#4e5c68",
			6: "#65707c"
		},
		text: {
			primary: "#012331",
			secondary: "#577885",
			disabled: "#676767e6"
		},
		background: {
			default: "#eaecee",
			paper: "#ffffff"
		}
	},
	components: {
		MuiAlert: { variants: [
			{
				props: {
					variant: "standard",
					severity: "error"
				},
				style: {
					backgroundColor: "#fbebe9",
					color: "#58180d"
				}
			},
			{
				props: {
					variant: "outlined",
					severity: "error"
				},
				style: (props) => ({
					borderColor: props.theme.palette.error.main,
					color: "#58180d"
				})
			},
			{
				props: {
					variant: "standard",
					severity: "warning"
				},
				style: {
					backgroundColor: "#f6f0d8",
					color: "#392d02"
				}
			},
			{
				props: {
					variant: "outlined",
					severity: "warning"
				},
				style: (props) => ({
					borderColor: props.theme.palette.warning.main,
					color: "#392d02"
				})
			},
			{
				props: {
					variant: "standard",
					severity: "info"
				},
				style: {
					backgroundColor: "#e9f5fe",
					color: "#392d02"
				}
			},
			{
				props: {
					variant: "outlined",
					severity: "info"
				},
				style: (props) => ({
					borderColor: props.theme.palette.info.main,
					color: "#392d02"
				})
			},
			{
				props: {
					variant: "standard",
					severity: "success"
				},
				style: {
					backgroundColor: "#edf7ed",
					color: "#1e4620"
				}
			},
			{
				props: {
					variant: "outlined",
					severity: "success"
				},
				style: (props) => ({
					borderColor: props.theme.palette.success.main,
					color: "#1e4620"
				})
			}
		] },
		MuiSlider: { styleOverrides: { valueLabel: (props) => ({
			padding: props.theme.spacing(.5, 1),
			fontSize: "0.75rem",
			fontWeight: 400,
			lineHeight: 1.16,
			letterSpacing: "0.03em",
			backgroundColor: props.theme.palette.muted[2],
			color: props.theme.palette.common.white
		}) } },
		MuiTooltip: { styleOverrides: {
			tooltip: (props) => ({
				padding: props.theme.spacing(.5, 1),
				fontSize: "0.75rem",
				fontWeight: 400,
				lineHeight: 1.16,
				letterSpacing: "0.03em",
				backgroundColor: props.theme.palette.muted[2],
				color: props.theme.palette.common.white
			}),
			arrow: (props) => ({
				color: props.theme.palette.muted[2],
				borderColor: props.theme.palette.muted[2]
			})
		} }
	}
};
const theme$2 = createTheme(deepmerge(themeOptions, themeOptions$2));
//#endregion
//#region src/styled.ts
const styled = createStyled({ defaultTheme: theme });
//#endregion
//#region src/components/FontAwesomeScalableIcon/FontAwesomeScalableIcon.tsx
/**
* A component to better render Font Awesome icons within MUI components. Taken
* from the MUI docs.
*
* @see https://codesandbox.io/p/sandbox/dr59e?file=%2Fsrc%2Fcomponents%2FFontAwesomeScalableIcon.tsx
*/
function FontAwesomeScalableIcon({ icon, ...props }) {
	return /* @__PURE__ */ jsx(SvgIcon, {
		...props,
		children: /* @__PURE__ */ jsx("svg", {
			width: "24",
			height: "24",
			viewBox: "0 0 24 20",
			preserveAspectRatio: "xMidYMid meet",
			children: /* @__PURE__ */ jsx(FontAwesomeIcon, { icon })
		})
	});
}
//#endregion
//#region src/components/IconButton/IconButton.tsx
/**
* Custom `IconButton` component based on the `IconButton` component from
* `mui`. All the same props are supported with the addition of a new `variant`
* prop based on the variants of the `Button` component. The `'default'` color
* was removed in favor of the `'neutral'` color.
*
* When using a Font Awesome icon as the children, the
* `FontAwesomeScalableIcon`'s `fontSize` prop **needs** to be `'inherit'`.
*
* @see https://v5.mui.com/material-ui/react-icon-button/
*/
const IconButton = styled((props) => /* @__PURE__ */ jsx(MuiIconButton, {
	color: "neutral",
	...props
}), { shouldForwardProp: (prop) => prop !== "variant" })(({ theme, color = "neutral", size = "medium", variant = "standard" }) => {
	const side = size === "small" ? 28 : size === "medium" ? 32 : 40;
	return {
		width: side,
		height: side,
		fontSize: size === "small" ? "1.125rem" : size === "medium" ? "1.25rem" : "1.5rem",
		transition: theme.transitions.create([
			"background-color",
			"box-shadow",
			"color",
			"border-color"
		], { duration: theme.transitions.duration.short }),
		"&.Mui-disabled": { color: theme.palette.action.disabled },
		...variant === "outlined" ? {
			border: `1px solid ${color !== "inherit" ? theme.palette[color].main : "currentColor"}`,
			"&.Mui-disabled": { borderColor: theme.palette.action.disabledBackground }
		} : {},
		...variant === "contained" && color !== "inherit" ? {
			backgroundColor: theme.palette[color].main,
			color: theme.palette[color].contrastText,
			boxShadow: theme.shadows[2],
			"&:hover, &:focus": {
				backgroundColor: theme.palette[color].dark,
				boxShadow: theme.shadows[4]
			},
			"&.Mui-disabled": { backgroundColor: theme.palette.action.disabledBackground }
		} : {}
	};
});
//#endregion
//#region src/components/Switch/Switch.tsx
/**
* Toggle the state between on and off. It is based on the `Switch` component
* from `mui`, so the same props are supported.
*
* @see https://v5.mui.com/material-ui/react-switch/
* @see https://v5.mui.com/material-ui/react-switch/#customization
*/
const Switch = styled((props) => /* @__PURE__ */ jsx(Switch$1, {
	focusVisibleClassName: ".Mui-focusVisible",
	disableRipple: false,
	...props
}))(({ theme, color, size }) => ({
	width: size === "small" ? 34 : 42,
	height: size === "small" ? 20 : 26,
	padding: 0,
	"& .MuiSwitch-switchBase": {
		padding: 0,
		margin: 2,
		transitionDuration: "300ms",
		"&.Mui-checked": {
			transform: `translateX(${size === "small" ? 14 : 16}px)`,
			color: theme.palette.common.white,
			"& + .MuiSwitch-track": {
				opacity: 1,
				border: 0
			},
			"&.Mui-disabled + .MuiSwitch-track": { opacity: .5 }
		},
		"&.Mui-focusVisible .MuiSwitch-thumb": {
			color: theme.palette[color ?? "primary"].main,
			border: `6px solid ${theme.palette.common.white}`
		},
		"&.Mui-disabled .MuiSwitch-thumb": { color: theme.palette.muted[3] },
		"&.Mui-disabled + .MuiSwitch-track": { opacity: .5 }
	},
	"& .MuiSwitch-thumb": {
		boxSizing: "border-box",
		width: size === "small" ? 16 : 22,
		height: size === "small" ? 16 : 22,
		backgroundColor: theme.palette.common.white
	},
	"& .MuiSwitch-track": {
		borderRadius: (size === "small" ? 20 : 26) / 2,
		backgroundColor: theme.palette.neutral.main,
		opacity: 1,
		transition: theme.transitions.create(["background-color"], { duration: 500 })
	}
}));
//#endregion
//#region src/components/Dialog/Confirmation.tsx
const slotProps = { paper: { sx: { maxWidth: 327 } } };
const titleSx = { maxWidth: "100%" };
const footerSx = {
	borderTopWidth: 1,
	borderTopStyle: "solid",
	borderTopColor: "divider"
};
function noop() {}
function Confirmation({ onGoBack, onDiscard }) {
	return /* @__PURE__ */ jsxs(Root, {
		open: true,
		onClose: noop,
		maxWidth: "xs",
		"aria-labelledby": "confirmation-modal-title",
		closeButton: null,
		slotProps,
		children: [/* @__PURE__ */ jsx(Header, { children: /* @__PURE__ */ jsx(Title, {
			id: "confirmation-modal-title",
			sx: titleSx,
			children: "Discard unsaved changes?"
		}) }), /* @__PURE__ */ jsxs(Footer, {
			sx: footerSx,
			children: [/* @__PURE__ */ jsx(Button, {
				type: "button",
				variant: "outlined",
				color: "neutral",
				size: "large",
				onClick: onDiscard,
				children: "Discard"
			}), /* @__PURE__ */ jsx(Button, {
				type: "button",
				variant: "contained",
				color: "secondary",
				size: "large",
				onClick: onGoBack,
				children: "Go back"
			})]
		})]
	});
}
//#endregion
//#region src/components/Dialog/Error.tsx
const SUPPORT_EMAIL = "bestservice@mend.com";
function Error(props) {
	return /* @__PURE__ */ jsxs(Root, {
		open: true,
		onClose: props.onClose,
		maxWidth: "sm",
		"aria-labelledby": "error-modal-title",
		children: [
			/* @__PURE__ */ jsx(Header, { children: /* @__PURE__ */ jsx(Title, {
				id: "error-modal-title",
				children: "Oops, it looks like something went wrong!"
			}) }),
			/* @__PURE__ */ jsx(Content, { children: /* @__PURE__ */ jsxs(Stack, {
				spacing: 1,
				direction: "column",
				children: [
					/* @__PURE__ */ jsx(Typography, { children: "We track these errors automatically. We're on it." }),
					/* @__PURE__ */ jsxs(Typography, { children: [
						"Please reach out to",
						" ",
						/* @__PURE__ */ jsx(Link, {
							href: `mailto:${SUPPORT_EMAIL}`,
							underline: "always",
							color: "text.primary",
							children: SUPPORT_EMAIL
						}),
						" ",
						"if the issue persists."
					] }),
					/* @__PURE__ */ jsx(Typography, { children: "Sorry for your inconvenience." })
				]
			}) }),
			/* @__PURE__ */ jsx(Footer, { children: /* @__PURE__ */ jsx(DismissButton, {
				color: "secondary",
				variant: "contained"
			}) })
		]
	});
}
//#endregion
//#region src/components/Dialog/Dialog.tsx
var Dialog_exports = /* @__PURE__ */ __exportAll({
	CloseButton: () => CloseButton,
	Content: () => Content,
	Description: () => Description,
	DismissButton: () => DismissButton,
	Footer: () => Footer,
	Header: () => Header,
	Root: () => Root,
	Title: () => Title
});
const ModalContext = React.createContext(null);
const useModalContext = () => React.useContext(ModalContext);
const LoaderWrapper = styled(Box)(({ theme }) => ({
	position: "absolute",
	inset: 0,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	backgroundColor: theme.palette.mode === "light" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)"
}));
const StyledDialog = styled(Dialog)(({ theme, fullScreen, fullWidth }) => {
	return {
		...theme.palette.mode === "dark" ? {
			[`& .${backdropClasses.root}`]: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
			[`& .${dialogClasses.paper}`]: { backgroundImage: "none" }
		} : {},
		...!fullScreen ? { [`& .${dialogClasses.paper}`]: {
			maxHeight: `calc(100% - (${theme.spacing(2)} * 2))`,
			width: fullWidth ? `calc(100% - (${theme.spacing(2)} * 2))` : void 0,
			margin: theme.spacing(2),
			borderRadius: Number(theme.shape.borderRadius) * 2.5,
			...theme.palette.mode === "dark" ? { backgroundImage: "none" } : {}
		} } : {}
	};
});
const defaultPaperPropsSx = { height: "100%" };
const Root = React.forwardRef(function Root({ children, closeButton, fullHeight = false, isLoading = false, onBoundaryError, onClose, requiresConfirmation = false, slotProps, ...props }, forwardedRef) {
	const onCloseRef = React.useRef(onClose);
	React.useLayoutEffect(() => {
		onCloseRef.current = onClose;
	});
	const requiresConfirmationRef = React.useRef(requiresConfirmation);
	React.useLayoutEffect(() => {
		requiresConfirmationRef.current = requiresConfirmation;
	});
	const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = React.useState(false);
	if (!props.open) setIsConfirmationDialogOpen(false);
	const handleClose = React.useCallback((reason) => {
		if (reason === "error" || reason === "discardChanges" || !(typeof requiresConfirmationRef.current === "boolean" ? requiresConfirmationRef.current : requiresConfirmationRef.current())) {
			onCloseRef.current(reason);
			return;
		}
		setIsConfirmationDialogOpen(true);
	}, []);
	const value = React.useMemo(() => ({ onClose: handleClose }), [handleClose]);
	return /* @__PURE__ */ jsx(ErrorBoundary, {
		onError: onBoundaryError,
		fallbackRender: ({ error }) => /* @__PURE__ */ jsx(Error, {
			error,
			onClose: () => handleClose("error")
		}),
		children: /* @__PURE__ */ jsx(ModalContext.Provider, {
			value,
			children: /* @__PURE__ */ jsxs(StyledDialog, {
				ref: forwardedRef,
				scroll: "paper",
				maxWidth: "md",
				disableEnforceFocus: isLoading,
				slotProps: {
					...slotProps,
					paper: fullHeight ? mergeSlotProps(slotProps?.paper, { sx: defaultPaperPropsSx }) : slotProps?.paper
				},
				...props,
				onClose: (_event, reason) => handleClose(reason),
				children: [
					children,
					isLoading && /* @__PURE__ */ jsx(LoaderWrapper, { children: /* @__PURE__ */ jsx(CircularProgress, {}) }),
					/* @__PURE__ */ jsx(FocusLock, {
						disabled: !isLoading,
						children: closeButton !== null && (closeButton ?? /* @__PURE__ */ jsx(CloseButton, {}))
					}),
					isConfirmationDialogOpen && /* @__PURE__ */ jsx(Confirmation, {
						onGoBack: () => setIsConfirmationDialogOpen(false),
						onDiscard: () => handleClose("discardChanges")
					})
				]
			})
		})
	});
});
/**
* Header
*/
const Header = styled(Box)(({ theme }) => ({ padding: theme.spacing(2) }));
const Title = styled(DialogTitle)(({ theme }) => ({
	padding: 0,
	maxWidth: `calc(100% - 28px - ${theme.spacing(2)})`
}));
const Description = styled((props) => /* @__PURE__ */ jsx(Typography, {
	variant: "body1",
	color: "text.secondary",
	...props
}))(({ theme }) => ({ marginTop: theme.spacing(.5) }));
/**
* Content
*/
const Content = styled((props) => /* @__PURE__ */ jsx(DialogContent, {
	dividers: true,
	...props
}))(({ theme }) => ({ padding: theme.spacing(2) }));
/**
* Footer
*/
const Footer = styled(DialogActions)(({ theme }) => ({
	padding: theme.spacing(2),
	gap: theme.spacing(1),
	"& > *:not(:first-of-type)": { margin: 0 },
	[theme.breakpoints.down("sm")]: {
		flexDirection: "column-reverse",
		alignItems: "stretch"
	}
}));
const DismissButton = React.forwardRef(function DismissButton(props, forwardedRef) {
	const { onClose } = useModalContext();
	return /* @__PURE__ */ jsx(Button, {
		ref: forwardedRef,
		type: "button",
		color: "neutral",
		variant: "outlined",
		size: "large",
		...props,
		onClick: () => onClose("dismissButtonClick"),
		children: props.children ?? "Dismiss"
	});
});
const StyledCloseButton = styled(IconButton)(({ theme }) => ({
	position: "absolute",
	right: theme.spacing(2),
	top: theme.spacing(2)
}));
const CloseButton = React.forwardRef(function CloseButton(props, forwardedRef) {
	const { onClose } = useModalContext();
	return /* @__PURE__ */ jsx(StyledCloseButton, {
		ref: forwardedRef,
		type: "button",
		"aria-label": "Dismiss",
		size: "small",
		color: "neutral",
		variant: "standard",
		autoFocus: true,
		disableFocusRipple: true,
		...props,
		onClick: () => onClose("closeButtonClick"),
		children: props.children ?? /* @__PURE__ */ jsx(Close, { fontSize: "small" })
	});
});
//#endregion
export { Dialog_exports as Dialog, FontAwesomeScalableIcon, IconButton, Switch, theme as baseTheme, themeOptions as baseThemeOptions, theme$1 as darkTheme, themeOptions$1 as darkThemeOptions, theme$2 as lightTheme, themeOptions$2 as lightThemeOptions, styled };
